import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";

const navItems = [
  { label: "서비스 소개", id: "how-it-works" },
  { label: "특징", id: "features" },
  { label: "요금제", id: "pricing" },
  { label: "고객 후기", id: "testimonials" },
  { label: "FAQ", id: "faq" },
];

export default function Navbar() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeId, setActiveId] = useState<string>("");
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
      const offsets = navItems
        .map(({ id }) => {
          const el = document.getElementById(id);
          if (!el) return null;
          return { id, top: el.getBoundingClientRect().top };
        })
        .filter(Boolean) as { id: string; top: number }[];
      const current = offsets
        .filter((o) => o.top <= 100)
        .sort((a, b) => b.top - a.top)[0];
      setActiveId(current?.id ?? "");
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // 유저 메뉴 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    const offset = 72;
    const top = el.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top, behavior: "smooth" });
    setMobileOpen(false);
  };

  const handleSignOut = async () => {
    setUserMenuOpen(false);
    try {
      await signOut();
      navigate("/");
    } catch (e) {
      console.error("[Navbar] 로그아웃 실패:", e);
    }
  };

  // 유저 이니셜 추출
  const getUserInitial = () => {
    const name = user?.user_metadata?.full_name as string | undefined;
    if (name) return name.charAt(0).toUpperCase();
    const email = user?.email ?? "";
    return email.charAt(0).toUpperCase();
  };

  const getUserDisplayName = () => {
    const name = user?.user_metadata?.full_name as string | undefined;
    return name ?? user?.email ?? "";
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-[#0d0d1a]/95 backdrop-blur-md border-b border-white/[0.06]" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        {/* Logo */}
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-2 cursor-pointer"
        >
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-gradient-to-br from-violet-500 to-pink-500 text-white font-bold text-sm">
            S
          </div>
          <span className="text-white font-semibold text-base whitespace-nowrap">Signal SMS</span>
        </button>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-1">
          {navItems.map(({ label, id }) => {
            const isActive = activeId === id;
            return (
              <button
                key={id}
                onClick={() => scrollToSection(id)}
                className={`relative px-4 py-2 rounded-lg text-sm transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  isActive ? "text-white" : "text-gray-400 hover:text-white hover:bg-white/8"
                }`}
              >
                {isActive && <span className="absolute inset-0 rounded-lg bg-white/10" />}
                <span className="relative">{label}</span>
                {isActive && (
                  <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-4 h-0.5 rounded-full bg-gradient-to-r from-violet-400 to-pink-400" />
                )}
              </button>
            );
          })}
        </nav>

        {/* Auth Buttons */}
        <div className="hidden md:flex items-center gap-2">
          {user ? (
            /* 로그인 상태 */
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 cursor-pointer group"
              >
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-pink-500 text-white text-sm font-semibold">
                  {getUserInitial()}
                </div>
                <span className="text-gray-300 text-sm group-hover:text-white transition-colors whitespace-nowrap max-w-[120px] truncate">
                  {getUserDisplayName()}
                </span>
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`ri-arrow-down-s-line text-gray-400 text-sm transition-transform ${userMenuOpen ? "rotate-180" : ""}`}></i>
                </div>
              </button>

              {/* Dropdown */}
              {userMenuOpen && (
                <div className="absolute right-0 top-full mt-2 w-48 bg-[#13131f] border border-white/[0.1] rounded-xl overflow-hidden">
                  <div className="px-4 py-3 border-b border-white/[0.06]">
                    <p className="text-white text-xs font-medium truncate">{getUserDisplayName()}</p>
                    <p className="text-gray-500 text-xs truncate mt-0.5">{user.email}</p>
                  </div>
                  <button
                    onClick={handleSignOut}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-gray-400 hover:text-white hover:bg-white/5 transition-all cursor-pointer text-sm whitespace-nowrap"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-logout-box-r-line text-sm"></i>
                    </div>
                    로그아웃
                  </button>
                </div>
              )}
            </div>
          ) : (
            /* 비로그인 상태 */
            <>
              <button
                onClick={() => navigate("/login")}
                className="text-gray-300 hover:text-white px-4 py-2 text-sm transition-colors cursor-pointer whitespace-nowrap"
              >
                로그인
              </button>
              <button
                onClick={() => scrollToSection("pricing")}
                className="bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-500 hover:to-pink-500 text-white px-5 py-2 rounded-lg text-sm font-medium transition-all hover:scale-105 cursor-pointer whitespace-nowrap"
              >
                무료 시작하기
              </button>
            </>
          )}
        </div>

        {/* Mobile Hamburger */}
        <button
          className="md:hidden w-8 h-8 flex items-center justify-center text-white cursor-pointer"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-xl`}></i>
        </button>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden overflow-hidden transition-all duration-300 ${
          mobileOpen ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
        } bg-[#0d0d1a] backdrop-blur-xl border-t border-white/10`}
      >
        <div className="px-6 py-4 flex flex-col gap-1">
          {navItems.map(({ label, id }) => {
            const isActive = activeId === id;
            return (
              <button
                key={id}
                onClick={() => scrollToSection(id)}
                className={`flex items-center gap-2.5 py-2.5 px-3 rounded-lg text-sm text-left cursor-pointer whitespace-nowrap transition-all ${
                  isActive ? "text-white bg-white/8" : "text-gray-400 hover:text-white hover:bg-white/5"
                }`}
              >
                {isActive && <span className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-violet-400 to-pink-400 shrink-0" />}
                {!isActive && <span className="w-1.5 h-1.5 rounded-full bg-white/20 shrink-0" />}
                {label}
              </button>
            );
          })}

          <div className="flex gap-2 mt-3 pt-3 border-t border-white/[0.08]">
            {user ? (
              <div className="flex-1 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 flex items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-pink-500 text-white text-xs font-semibold shrink-0">
                    {getUserInitial()}
                  </div>
                  <span className="text-gray-300 text-sm truncate max-w-[140px]">{getUserDisplayName()}</span>
                </div>
                <button
                  onClick={handleSignOut}
                  className="text-gray-400 hover:text-white text-sm cursor-pointer whitespace-nowrap transition-colors flex items-center gap-1"
                >
                  <i className="ri-logout-box-r-line text-sm"></i>
                  로그아웃
                </button>
              </div>
            ) : (
              <>
                <button
                  onClick={() => { navigate("/login"); setMobileOpen(false); }}
                  className="flex-1 border border-white/20 text-white py-2.5 rounded-lg text-sm cursor-pointer whitespace-nowrap hover:bg-white/5 transition-colors"
                >
                  로그인
                </button>
                <button
                  onClick={() => scrollToSection("pricing")}
                  className="flex-1 bg-gradient-to-r from-violet-600 to-pink-600 text-white py-2.5 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap"
                >
                  무료 시작하기
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}